import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

export const Route = createFileRoute("/help")({
	component: HelpLayout,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/help" });

		return {
			meta: [
				{ title: "AgentFramer | Docs" },
				{
					name: "description",
					content:
						"Learn how to connect AgentFramer to your AI assistant and create images, videos, and audio through MCP.",
				},
				{ property: "og:title", content: "AgentFramer | Docs" },
				{
					property: "og:description",
					content:
						"Setup guides and MCP references for creating content assets with AgentFramer.",
				},
				{ property: "og:image", content: ogImageUrl("help") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("help") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/help" }),
		};
	},
});

export const helpSections = [
	{
		group: "Getting started",
		items: [
			{
				slug: "quick-start",
				label: "Quick start",
				keywords: [
					"quickstart",
					"setup",
					"install",
					"connect",
					"first generation",
				],
			},
			{
				slug: "what-is-agentframer",
				label: "How it works",
				keywords: ["overview", "architecture", "concepts", "agentframer"],
			},
		],
	},
	{
		group: "MCP",
		items: [
			{
				slug: "mcp",
				label: "MCP overview",
				keywords: ["mcp", "protocol", "client", "assistant"],
			},
			{
				slug: "mcp-tools",
				label: "MCP tools reference",
				keywords: [
					"tools",
					"generate_image",
					"generate_video",
					"generate_audio",
					"api",
				],
			},
			{
				slug: "tool-call-patterns",
				label: "Tool call patterns",
				keywords: ["patterns", "agent workflow", "recovery", "model override"],
			},
		],
	},
	{
		group: "Team",
		items: [
			{
				slug: "inviting-members",
				label: "Inviting members",
				keywords: ["team", "invite", "collaboration"],
			},
			{
				slug: "roles",
				label: "Roles and permissions",
				keywords: ["roles", "permissions", "access"],
			},
			{
				slug: "workspace-access",
				label: "Workspace access",
				keywords: ["workspace", "switch", "active workspace"],
			},
		],
	},
];

export function DocsSidebar({
	currentSlug,
	basePath = "/help",
}: {
	currentSlug: string | null;
	basePath?: string;
}) {
	return (
		<nav aria-label="Documentation" className="space-y-5">
			{helpSections.map((section) => (
				<div key={section.group}>
					<p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
						{section.group}
					</p>
					<ul className="space-y-0.5">
						{section.items.map((item) => {
							const isActive = currentSlug === item.slug;
							return (
								<li key={item.slug}>
									<a
										href={`${basePath}/${item.slug}`}
										className={`block rounded-md px-2 py-1.5 text-sm transition-colors ${
											isActive
												? "bg-brand-accent/10 font-medium text-brand-accent"
												: "text-muted-foreground hover:bg-accent hover:text-foreground"
										}`}
									>
										{item.label}
									</a>
								</li>
							);
						})}
					</ul>
				</div>
			))}
		</nav>
	);
}

export const DEFAULT_TOC = [
	"Overview",
	"Prerequisites",
	"Configuration",
	"Next steps",
];

export const PAGE_TOC: Record<string, string[]> = {
	"quick-start": [
		"Setting up MCP",
		"Connecting your agent",
		"Your first generation",
	],
	mcp: ["What is MCP?", "How it works", "Setting it up", "Workspaces and MCP"],
	"mcp-tools": [
		"Available tools",
		"Generate",
		"Models",
		"Billing",
		"Workspaces",
	],
	"tool-call-patterns": [
		"One-shot generation",
		"Model override",
		"Recovering previous output",
	],
	"inviting-members": ["Sending invites", "Pending invitations", "Roles"],
	roles: ["Role overview", "Permissions", "Changing roles"],
	"workspace-access": [
		"Access rules",
		"Active workspace",
		"Recommended assistant flow",
	],
	"what-is-agentframer": ["How it works", "Key concepts", "Architecture"],
};

function HelpLayout() {
	const matches = useMatches();
	const lastMatch = matches[matches.length - 1];
	const currentSlug = lastMatch?.fullPath.replace("/help/", "") || null;
	const currentLabel =
		helpSections
			.flatMap((section) => section.items)
			.find((item) => item.slug === currentSlug)?.label ?? "Browse topics";

	return (
		<div className="relative flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<main className="relative z-10 flex-1">
				<div className="mx-auto max-w-6xl px-4 sm:px-6">
					<div className="marketing-line-stack flex flex-col overflow-hidden divide-y divide-border">
						<div className="relative overflow-hidden rounded-md bg-white px-6 py-10 dark:bg-black sm:px-10 sm:py-12">
							<div className="relative flex gap-12">
								<aside className="hidden w-52 shrink-0 self-start lg:block">
									<div className="sticky top-6">
										<DocsSidebar currentSlug={currentSlug} />
									</div>
								</aside>

								<div className="min-w-0 flex-1">
									<details className="group mb-6 rounded-md border border-border bg-background/60 lg:hidden">
										<summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-medium text-foreground [&::-webkit-details-marker]:hidden">
											<span className="truncate">{currentLabel}</span>
											<span className="ml-2 text-muted-foreground transition-transform group-open:rotate-180">
												&#9662;
											</span>
										</summary>
										<div className="border-t border-border px-4 py-4">
											<DocsSidebar currentSlug={currentSlug} />
										</div>
									</details>
									<Outlet />
								</div>
							</div>
						</div>
					</div>
				</div>
			</main>
			<PublicFooter showCta={false} />
		</div>
	);
}
